import { dateHandler } from './date-utils';
import { setZeroHours } from './general-utils';

/**
 * Utility functions focused on payment calculations
 * Extracted and refactored for single responsibility and testability
 */

/**
 * Calculates snowball data for an account based on its position and previous accounts
 */
export const calculateSnowballData = (accounts, currentAccountIndex, snowballStarterLump = 0) => {
    // First account gets the starter lump sum
    if (currentAccountIndex === 0 || !currentAccountIndex) {
        return {
            paymentNumber: 0,
            paymentDate: accounts[currentAccountIndex].paymentSchedule?.[0]?.date || new Date(),
            paymentAmount: snowballStarterLump,
            isFirst: true
        };
    }

    const currentAccount = accounts[currentAccountIndex];
    const previousAccount = accounts[currentAccountIndex - 1];
    
    // Calculate cumulative payment amount from accounts that are paid off
    const cumulativePaymentAmount = accounts.reduce((accumulator, account, index) => {
        const isAccountPaidOff = (
            currentAccount.paymentSchedule &&
            account.paymentSchedule &&
            account.paymentSchedule.length < currentAccount.paymentSchedule.length
        );
        
        return isAccountPaidOff ? accumulator + account.defaultPaymentAmount : accumulator;
    }, 0);

    // Get snowball timing from previous account's final payment
    if (previousAccount?.paymentSchedule?.length > 0) {
        const finalPayment = previousAccount.paymentSchedule[previousAccount.paymentSchedule.length - 1];
        
        return {
            paymentNumber: finalPayment.paymentNumber,
            paymentDate: finalPayment.date,
            paymentAmount: cumulativePaymentAmount + snowballStarterLump
        };
    }

    return null;
};

/**
 * Applies snowball payment to base payment amount based on timing
 */
export const applySnowballPayment = (basePayment, prevBalance, interestPaid, paymentDate, snowballData) => {
    if (!snowballData || paymentDate < snowballData.paymentDate) {
        return basePayment;
    }

    // Apply starter lump sum to first payment
    if (snowballData.paymentNumber === 0 && snowballData.isFirst) {
        return basePayment + snowballData.paymentAmount;
    }
    
    const paymentDateObj = new Date(paymentDate);
    const snowballDateObj = new Date(snowballData.paymentDate);
    
    // Apply snowball if payment is after snowball date
    if (paymentDateObj >= snowballDateObj) {
        const totalPayment = basePayment + snowballData.paymentAmount;
        
        // Cap payment at remaining balance plus interest
        return Math.min(totalPayment, prevBalance + interestPaid);
    }

    return basePayment;
};

/**
 * Calculates interest rate for a specific payment date considering intro periods
 */
export const calculateInterestRate = (baseRate, paymentDate, introRateEndDate, introRate = 0) => {
    if (!introRateEndDate) {
        return baseRate;
    }

    const introEndTime = new Date(introRateEndDate).getTime();
    return paymentDate <= introEndTime ? introRate : baseRate;
};

/**
 * Calculates minimum payment based on account terms
 */
export const calculateMinimumPayment = (balance, minPaymentPercentage, minPaymentForLowBalance) => {
    return Math.max(
        minPaymentPercentage * balance,
        minPaymentForLowBalance
    );
};

/**
 * Determines payment amount based on payment type and account settings
 */
export const calculatePaymentAmount = (account, prevBalance, interestPaid) => {
    const {
        paymentType,
        fixed_payment: fixedPayment,
        minimum_payment_percentage: minPaymentPercentage,
        minimum_payment_low_balance: minPaymentForLowBalance
    } = account;

    let payment;

    if (paymentType === "minimum" || !fixedPayment || fixedPayment === 0) {
        payment = calculateMinimumPayment(prevBalance, minPaymentPercentage, minPaymentForLowBalance);
    } else {
        payment = fixedPayment;
    }

    // Cap payment at balance plus interest
    return Math.min(payment, prevBalance + interestPaid);
};

/**
 * Validates that a payment is sufficient to make progress
 */
export const validatePaymentProgress = (payment, interestPaid, prevBalance) => {
    if (prevBalance > 0 && payment <= interestPaid) {
        return {
            isValid: false,
            error: 'Payment is too low. Balance will never be paid off.'
        };
    }
    
    return { isValid: true, error: null };
};

/**
 * Calculates a single payment row for the schedule
 */
export const calculatePaymentRow = (paymentNumber, paymentDate, prevBalance, account) => {
    const {
        intro_rate_end_date: introRateEndDate,
        intro_apr: introRate,
        apr: baseInterestRate,
        extraPayments = [],
        snowballData
    } = account;

    // Increment payment date by one month
    const date = new Date(paymentDate);
    date.setMonth(date.getMonth() + 1);

    // Calculate interest rate (considering intro periods)
    const interestRate = calculateInterestRate(baseInterestRate, date, introRateEndDate, introRate);
    
    // Calculate interest owed
    const interestPaid = (interestRate / 12) * prevBalance;
    
    // Calculate base payment
    let payment = calculatePaymentAmount(account, prevBalance, interestPaid);
    
    // Apply snowball effect
    payment = applySnowballPayment(payment, prevBalance, interestPaid, date, snowballData);
    
    // Add extra payments
    const extraPayment = extraPayments.find(ep => ep.paymentNumber === paymentNumber)?.amount || 0;
    const totalPayment = payment + extraPayment;
    
    // Calculate principal and remaining balance
    const principalPaid = totalPayment - interestPaid;
    let balance = prevBalance - principalPaid;
    
    // Validate payment progress
    const validation = validatePaymentProgress(totalPayment, interestPaid, prevBalance);
    if (!validation.isValid) {
        return { error: validation.error };
    }
    
    // Round very small balances to zero
    if (balance < 0.01) {
        balance = 0;
    }

    return {
        paymentNumber,
        date,
        rate: interestRate,
        payment,
        extraPayment,
        totalPayment,
        interestPaid,
        principalPaid,
        balance,
        prevBalance
    };
};

/**
 * Calibrates the first payment date based on due date and current date
 */
export const calibrateFirstPaymentDate = (dueDate) => {
    const currentDate = new Date();
    const paymentDate = new Date();
    
    paymentDate.setDate(dueDate);
    
    // If payment date hasn't passed this month, start from last month
    if (paymentDate.getDate() >= currentDate.getDate() && 
        paymentDate.getMonth() === currentDate.getMonth()) {
        paymentDate.setMonth(paymentDate.getMonth() - 1);
    }

    return setZeroHours(paymentDate);
};